import React, { useRef, useEffect, useCallback } from 'react';

// YouTube video ID for the "Project Map Overview" demo (see #154, #217).
// True 16:9 (3840x2160) re-export — fills the frame, no black bars.
const OVERVIEW_VIDEO_ID = '7U-Up0YjMtQ';

// Autoplay MUTED with visible controls and no loop. Muted autoplay is permitted
// by browser autoplay policies in both trigger paths (help button + ?overview=1
// page load), and the video has baked-in captions so it communicates fully even
// while muted. Users can unmute via the native player controls.
// youtube-nocookie.com keeps this a privacy-enhanced embed.
const EMBED_SRC =
  `https://www.youtube-nocookie.com/embed/${OVERVIEW_VIDEO_ID}` +
  '?autoplay=1&mute=1&controls=1&rel=0&modestbranding=1&playsinline=1';

/**
 * Overview video modal.
 *
 * Contents are ONLY the embedded video + an "X" close button in the upper-right
 * corner — nothing else. Mirrors the backdrop / Esc-to-close / click-outside
 * behavior of the map's other modals (ProUpgradeModal, ExportDataModal,
 * SavedSearchesModal).
 *
 * Standard responsive 16:9 embed: the player box is a plain 16:9 container
 * (capped by the viewport so it never overflows) and the iframe simply fills
 * 100% of it — no cropping, scaling, or transforms. This assumes a true 16:9
 * source video; the current placeholder id is the older ~16:10 export, so black
 * bars will show until the new true-16:9 re-export's id replaces it above.
 */
function OverviewVideoModal({ isOpen, onClose }) {
  const modalRef = useRef(null);

  // Close on Escape.
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Close on click outside the modal.
  const handleBackdropClick = useCallback((e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  }, [onClose]);

  // Unmount the iframe when closed so playback (and audio) stops.
  if (!isOpen) return null;

  return (
    <div className="overview-video-modal-backdrop" onClick={handleBackdropClick}>
      <div
        className="overview-video-modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Map overview video"
      >
        <button
          className="overview-video-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M2 2L18 18M2 18L18 2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="overview-video-modal__player">
          <iframe
            className="overview-video-modal__frame"
            src={EMBED_SRC}
            title="Map overview video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}

export default OverviewVideoModal;
